import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LineField } from "../components/LineField";
import { PaginationBar } from "../components/Pagination";
import { useAuth } from "../hooks/useAuth";
import { fetchAuditLog } from "../api/admin";
import { C, font } from "../styles/tokens";

/**
 * AdminAuditLogPage - filterable view of platform audit log.
 *
 * Filters: resourceType, action (substring match), actorUserId.
 * Pagination via limit/offset.
 *
 * Each row shows: timestamp, actor, action, resource, ip address, and a
 * "metadata" toggle that expands to show the full JSON payload (which is
 * where the diff / context lives).
 */
export function AdminAuditLogPage() {
  const { localUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = localUser?.type === "PLATFORM_ADMIN";

  const [filters, setFilters] = useState({
    resourceType: "",
    action: "",
    actorUserId: "",
    limit: 50,
    offset: 0,
  });
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  async function reload(newFilters) {
    setLoading(true);
    try {
      const result = await fetchAuditLog({
        resourceType: newFilters.resourceType || undefined,
        action: newFilters.action || undefined,
        actorUserId: newFilters.actorUserId || undefined,
        limit: newFilters.limit,
        offset: newFilters.offset,
      });
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message ?? "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) reload(filters);
  }, [isAdmin]);

  function applyFilters() {
    const f = { ...filters, offset: 0 };
    setFilters(f);
    reload(f);
  }

  if (!isAdmin) {
    return (
      <AppShell>
        <div style={cardStyle}>This page is for platform administrators.</div>
      </AppShell>
    );
  }

  // Server-side pager: page/size changes refetch with new limit/offset, so
  // the browser only ever holds one page of entries.
  const pager = {
    page: Math.floor(filters.offset / filters.limit),
    pageSize: filters.limit,
    total: data.total,
    totalPages: Math.max(1, Math.ceil(data.total / filters.limit)),
    setPage: (p) => {
      const f = { ...filters, offset: p * filters.limit };
      setFilters(f);
      reload(f);
    },
    setPageSize: (n) => {
      const f = { ...filters, limit: n, offset: 0 };
      setFilters(f);
      reload(f);
    },
  };

  return (
    <AppShell maxWidth={1100}>
      <div style={{ marginBottom: 16 }}>
        <button
          onClick={() => navigate("/admin")}
          style={{
            background: "transparent",
            border: 0,
            color: C.blue,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            padding: "4px 8px",
            fontFamily: font,
          }}
        >
          Back to admin
        </button>
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>
        Audit log
      </h1>

      {/* Filters */}
      <section style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          data-responsive-grid="3"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12,
          }}
        >
          <LineField
            label="Resource type"
            placeholder="e.g. VerificationRequest"
            value={filters.resourceType}
            onChange={(e) =>
              setFilters((f) => ({ ...f, resourceType: e.target.value }))
            }
          />
          <LineField
            label="Action (substring)"
            placeholder="e.g. approved"
            value={filters.action}
            onChange={(e) =>
              setFilters((f) => ({ ...f, action: e.target.value }))
            }
          />
          <LineField
            label="Actor user ID"
            placeholder="cuid string"
            value={filters.actorUserId}
            onChange={(e) =>
              setFilters((f) => ({ ...f, actorUserId: e.target.value }))
            }
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            onClick={applyFilters}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: C.blue,
              border: 0,
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: font,
            }}
          >
            Apply filters
          </button>
          <button
            onClick={() => {
              const cleared = {
                resourceType: "",
                action: "",
                actorUserId: "",
                limit: 50,
                offset: 0,
              };
              setFilters(cleared);
              reload(cleared);
            }}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: C.textMed,
              background: "transparent",
              border: `1px solid ${C.dividerStrong}`,
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: font,
            }}
          >
            Clear
          </button>
        </div>
      </section>

      {error && (
        <div style={{ ...cardStyle, color: C.red, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Results */}
      <section style={cardStyle}>
        {loading ? (
          <div
            style={{ color: C.textMed, padding: "20px 0", textAlign: "center" }}
          >
            Loading...
          </div>
        ) : data.entries.length === 0 ? (
          <div
            style={{ color: C.textMed, padding: "20px 0", textAlign: "center" }}
          >
            No audit log entries match these filters.
          </div>
        ) : (
          <>
            <div style={{ borderTop: `1px solid ${C.divider}` }}>
              {data.entries.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={entry}
                  expanded={expandedRow === entry.id}
                  onToggle={() =>
                    setExpandedRow(expandedRow === entry.id ? null : entry.id)
                  }
                />
              ))}
            </div>
            <PaginationBar
              pager={pager}
              itemLabel="entries"
              style={{ margin: "0 -20px -20px", borderRadius: "0 0 8px 8px" }}
            />
          </>
        )}
      </section>
    </AppShell>
  );
}

function AuditRow({ entry, expanded, onToggle }) {
  const actorLabel = entry.actor
    ? `${entry.actor.email} (${labelType(entry.actor.type)})`
    : entry.actorType === "SYSTEM"
      ? "System"
      : "Unknown";

  return (
    <div style={{ borderBottom: `1px solid ${C.divider}`, padding: "10px 0" }}>
      <div
        onClick={onToggle}
        style={{
          display: "grid",
          gridTemplateColumns: "140px 1fr 1fr 1fr 80px",
          gap: 12,
          alignItems: "center",
          cursor: "pointer",
          fontSize: 12,
        }}
      >
        <div style={{ color: C.textLight, fontVariantNumeric: "tabular-nums" }}>
          {formatDateTime(entry.createdAt)}
        </div>
        <div style={{ color: C.textDark, fontWeight: 600 }}>{entry.action}</div>
        <div
          style={{
            color: C.textMed,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {actorLabel}
        </div>
        <div
          style={{ color: C.textMed, fontFamily: "monospace", fontSize: 11 }}
        >
          {entry.resourceType}
          <span style={{ color: C.textLight }}>
            {" "}
            / {entry.resourceId.slice(0, 12)}...
          </span>
        </div>
        <div style={{ textAlign: "right", color: C.blue, fontWeight: 600 }}>
          {expanded ? "Hide" : "Details"}
        </div>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 8,
            padding: 12,
            background: C.bgMuted,
            borderRadius: 4,
            fontSize: 11,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            color: C.textDark,
            lineHeight: 1.5,
          }}
        >
          {JSON.stringify(
            {
              id: entry.id,
              resourceId: entry.resourceId,
              ipAddress: entry.ipAddress,
              userAgent: entry.userAgent,
              metadata: entry.metadata,
            },
            null,
            2,
          )}
        </div>
      )}
    </div>
  );
}

function labelType(t) {
  return (
    {
      PROFESSIONAL: "Professional",
      INSTITUTION_OFFICER: "Institution",
      ORGANISATION_OFFICER: "Organisation",
      PLATFORM_ADMIN: "Admin",
    }[t] ?? t
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 20,
};
